import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Briefcase } from "lucide-react";
import api from "@/lib/api";
import { Spinner, EmptyState, Pagination, DemoBadge } from "@/components/common";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ALL = "__all__";

export default function Members() {
  const [params, setParams] = useSearchParams();
  const [industries, setIndustries] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const industry = params.get("industry") || "";
  const city = params.get("city") || "";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setIndustries(data.industries)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/members", {
        params: { search: search || undefined, industry: industry || undefined, city: city || undefined, page },
      });
      setData(data);
    } catch {
      setData({ items: [], total: 0, page: 1, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [search, industry, city, page]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
    });
    if (!("page" in patch)) next.set("page", "1");
    setParams(next);
  };

  return (
    <div className="container-nx section-nx">
      <div className="label-eyebrow mb-2">Member Directory</div>
      <h1 className="heading-2">Discover community members</h1>
      <p className="mt-3 max-w-2xl text-base text-slate-500">
        Search and connect with entrepreneurs, professionals and business owners across the community.
      </p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && update({ search })}
            placeholder="Search by name, business or expertise..."
            className="input-nx pl-10"
            data-testid="member-search-input"
          />
        </div>
        <input
          defaultValue={city}
          onKeyDown={(e) => e.key === "Enter" && update({ city: e.target.value })}
          onBlur={(e) => e.target.value !== city && update({ city: e.target.value })}
          placeholder="City"
          className="input-nx sm:w-40"
          data-testid="member-city-input"
        />
        <Select value={industry || ALL} onValueChange={(v) => update({ industry: v === ALL ? "" : v })}>
          <SelectTrigger className="input-nx h-auto sm:w-64" data-testid="member-industry-select">
            <SelectValue placeholder="All industries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All industries</SelectItem>
            {industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => update({ search })} className="btn-navy" data-testid="member-search-btn">Search</button>
      </div>

      <div className="mt-8">
        {loading || !data ? (
          <Spinner label="Loading members..." />
        ) : data.items.length === 0 ? (
          <EmptyState title="No members found" message="Try adjusting your search or filters, or be the first to invite others." icon={Briefcase} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.items.map((m) => (
                <Link key={m.id} to={`/members/${m.id}`} className="card-nx card-nx-hover p-6" data-testid={`member-card-${m.id}`}>
                  <div className="flex items-start gap-4">
                    <Avatar name={m.name} src={m.photo_url} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-semibold text-[#0B192C] font-head">{m.name}</h3>
                        {m.is_demo && <DemoBadge />}
                      </div>
                      <p className="truncate text-sm text-slate-500">{m.designation || m.profession || "Community member"}</p>
                      {m.city && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                          <MapPin className="h-3 w-3" /> {m.city}
                        </p>
                      )}
                    </div>
                  </div>
                  {m.industry && <span className="chip-gold mt-4">{m.industry}</span>}
                  {m.intro && <p className="mt-3 line-clamp-2 text-sm text-slate-500">{m.intro}</p>}
                </Link>
              ))}
            </div>
            <Pagination page={data.page} pages={data.pages} onChange={(p) => update({ page: String(p) })} />
          </>
        )}
      </div>
    </div>
  );
}

export function Avatar({ name, src, size = "h-14 w-14" }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  if (src) {
    return <img src={src} alt={name} className={`${size} shrink-0 rounded-full object-cover ring-2 ring-[#D4AF37]/30`} />;
  }
  return (
    <div className={`${size} flex shrink-0 items-center justify-center rounded-full bg-[#2563EB] text-sm font-bold text-white`}>
      {initials}
    </div>
  );
}
